import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ProgramOffering,
  ProgramOfferingInput,
  StudioLocation,
  StudioLocationInput,
} from '@pirouette/shared';
import { programOfferingInputSchema, studioLocationInputSchema } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { stripUndefined } from '@/lib/db';

export function usePrograms() {
  return useQuery({
    queryKey: ['programs'],
    queryFn: async (): Promise<ProgramOffering[]> => {
      const { data, error } = await supabase
        .from('program_offerings')
        .select('*')
        .is('deleted_at', null)
        .order('program_type', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useProgram(id: string | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: ['programs', id],
    queryFn: async (): Promise<ProgramOffering | null> => {
      const { data, error } = await supabase
        .from('program_offerings')
        .select('*')
        .eq('id', id!)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });
}

export function useCreateProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ProgramOfferingInput): Promise<ProgramOffering> => {
      const parsed = programOfferingInputSchema.parse(input);
      const { data, error } = await supabase
        .from('program_offerings')
        .insert(stripUndefined(parsed))
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['programs'] });
    },
  });
}

export function useLocations() {
  return useQuery({
    queryKey: ['locations'],
    queryFn: async (): Promise<StudioLocation[]> => {
      const { data, error } = await supabase
        .from('studio_locations')
        .select('*')
        .is('deleted_at', null)
        .order('name', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: StudioLocationInput): Promise<StudioLocation> => {
      const parsed = studioLocationInputSchema.parse(input);
      const { data, error } = await supabase
        .from('studio_locations')
        .insert(stripUndefined(parsed))
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['locations'] });
    },
  });
}
